import {
  UserIcon,
  BuildingOffice2Icon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import GreenIdScreeningSummary from './GreenIdScreeningSummary'
import {
  getMatchDetailTitle,
  getMatchCardSubtitle,
  isWatchlistAmlScreening
} from '../../utils/amlMatchPresentation'
import { sanitizeVendorBranding } from '../../utils/sanitizeVendorBranding'
import { formatThreatLevel } from '../../utils/amlResultCategories'

/**
 * Provider-aware AML match detail modal (watchlist hits vs reference-database person entity).
 */
export default function AmlMatchDetailModal({
  match,
  screeningSummary = null,
  provider = null,
  onClose,
  getRiskBadgeColor,
  getRiskLevelColor,
  getConfidenceColor,
  getThreatLevelColor
}) {
  if (!match) return null

  const watchlistScreening = isWatchlistAmlScreening(match, provider)
  const detailTitle = getMatchDetailTitle(match)
  const subtitle = getMatchCardSubtitle(match)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[200]">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              {match.matchKind === 'person' || match.gender ? (
                <UserIcon className="h-6 w-6 mr-2 shrink-0" />
              ) : (
                <BuildingOffice2Icon className="h-6 w-6 mr-2 shrink-0" />
              )}
              <span>{detailTitle}</span>
            </h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>

          {watchlistScreening && screeningSummary && (
            <GreenIdScreeningSummary summary={screeningSummary} compact />
          )}

          {watchlistScreening && match.detailKindLabel && (
            <p className="text-sm text-slate-600 mb-4">
              Hit type: <strong>{match.detailKindLabel}</strong>
              {match.matchKind === 'pep_group' && match.sourceListName && (
                <span className="block mt-1 text-gray-600">
                  List: {match.sourceListName}
                </span>
              )}
            </p>
          )}

          {subtitle && watchlistScreening && (
            <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-gray-800">
              {match.matchKind === 'media_article' ? (
                <>
                  <span className="font-medium text-gray-700">Headline</span>
                  <p className="mt-1">{match.displayTitle || subtitle}</p>
                </>
              ) : (
                subtitle
              )}
            </div>
          )}

          {match.riskLevel && (
            <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                Risk assessment
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div
                    className={`inline-block px-3 py-2 text-sm font-bold rounded ${getRiskLevelColor(match.riskLevel)}`}
                  >
                    {match.riskLevel}
                    {!String(match.riskLevel).toLowerCase().includes('risk') ? ' Risk' : ''}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Overall risk level</div>
                </div>
                {match.threatLevel && (
                  <div className="text-center">
                    <div
                      className={`inline-block px-3 py-2 text-sm font-medium rounded border ${getThreatLevelColor(formatThreatLevel(match.threatLevel))}`}
                    >
                      {formatThreatLevel(match.threatLevel)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Watchlist status</div>
                  </div>
                )}
                <div className="text-center">
                  <div
                    className={`inline-block px-3 py-2 text-sm font-medium rounded ${getRiskBadgeColor(match.riskCategory)}`}
                  >
                    {match.riskCategory}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Category</div>
                </div>
              </div>
            </div>
          )}

          {watchlistScreening ? (
            <WatchlistHitDetail match={match} getConfidenceColor={getConfidenceColor} />
          ) : (
            <ReferenceDatabaseHitDetail
              match={match}
              getRiskBadgeColor={getRiskBadgeColor}
              getConfidenceColor={getConfidenceColor}
            />
          )}

          {match.profileNotes && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Source notes</h3>
              <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200 text-sm text-gray-700 leading-relaxed">
                {String(sanitizeVendorBranding(match.profileNotes) || '')
                  .split(/(?<=\.)\s+/)
                  .map((line, index) => (
                    <p key={index} className={index > 0 ? 'mt-2' : ''}>
                      {line}
                    </p>
                  ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Generate Report
            </button>
            <button type="button" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              Create Case
            </button>
            <button type="button" className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function WatchlistHitDetail({ match, getConfidenceColor }) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">This hit</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-gray-600">Match kind</dt>
          <dd className="font-medium text-gray-900">
            {(match.detailKindLabel || match.matchKind || '—').replace(/_/g, ' ')}
          </dd>
        </div>
        <div>
          <dt className="text-gray-600">Confidence</dt>
          <dd className={`font-medium ${getConfidenceColor(match.confidence)}`}>
            {match.confidence}%
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-gray-600">Watchlist / source</dt>
          <dd className="text-gray-900">{match.riskDescription || '—'}</dd>
        </div>
        {match.greenidVerificationId && (
          <div>
            <dt className="text-gray-600">Screening reference</dt>
            <dd className="font-mono text-gray-900">{match.greenidVerificationId}</dd>
          </div>
        )}
      </dl>
      {(match.matchKind === 'pep_group' || match.matchKind === 'watchlist_list') && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3">
          This row is a <strong>watchlist group</strong>, not a separate person record. PEP status for
          the screened subject comes from membership in lists like this one, plus structured profile
          matches at screening level.
        </p>
      )}
      {match.matchKind === 'media_article' && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3">
          Names in the headline may refer to the subject or to third parties. Confirm context before
          escalation.
        </p>
      )}
      {match.matchKind === 'person' && /^ASSOCIATE/i.test(String(match.riskCategory || '')) && (
        <p className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded p-3">
          This is a <strong>related person (associate)</strong> returned by screening, not necessarily
          the person you searched for.
        </p>
      )}
    </div>
  )
}

function ReferenceDatabaseHitDetail({ match, getRiskBadgeColor, getConfidenceColor }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Basic information</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <span className="font-medium text-gray-700">Risk category</span>
            <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskBadgeColor(match.riskCategory)}`}>
              {match.riskCategory} — {match.riskDescription}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Match confidence</span>
            <span className={`font-medium ${getConfidenceColor(match.confidence)}`}>
              {match.confidence}%
            </span>
          </div>
          {match.gender && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Gender</span>
              <span className="text-gray-600">{match.gender}</span>
            </div>
          )}
          {match.nationality && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Nationality</span>
              <span className="text-gray-600">{match.nationality}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Status</span>
            <span className={match.active ? 'text-green-600' : 'text-red-600'}>
              {match.active ? 'Active' : 'Inactive'}
            </span>
          </div>
          {match.lastUpdated && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Last updated</span>
              <span className="text-gray-600">{match.lastUpdated}</span>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Profile data</h3>
        {match.countries?.length > 0 && (
          <div className="text-sm">
            <span className="font-medium text-gray-700">Countries: </span>
            {match.countries.join(', ')}
          </div>
        )}
        {match.aliases?.length > 0 && (
          <div className="text-sm">
            <span className="font-medium text-gray-700 block mb-1">Aliases</span>
            {match.aliases.map((a, i) => (
              <div key={i} className="text-gray-600 bg-gray-50 px-2 py-1 rounded mb-1">
                {a}
              </div>
            ))}
          </div>
        )}
        {match.roles?.length > 0 && (
          <div className="text-sm">
            <span className="font-medium text-gray-700 block mb-1">Roles</span>
            {match.roles.map((r, i) => (
              <div key={i} className="text-blue-700 bg-blue-50 px-2 py-1 rounded mb-1">
                {r}
              </div>
            ))}
          </div>
        )}
        {match.sanctions?.length > 0 && (
          <div className="text-sm">
            <span className="font-medium text-red-700 block mb-1">Sanctions</span>
            {match.sanctions.map((s, i) => (
              <div key={i} className="text-red-600 bg-red-50 px-2 py-1 rounded mb-1">
                {s}
              </div>
            ))}
          </div>
        )}
        {!match.aliases?.length && !match.roles?.length && !match.countries?.length && (
          <p className="text-sm text-gray-500">No extended profile fields on this record.</p>
        )}
      </div>
    </div>
  )
}
